import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import Avatar from "../components/Avatar";
import TierBadge from "../components/TierBadge";
import { useAuth } from "../contexts/AuthContext";
import { updateProfile, getProfileStats, getStatus } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Edit3, Check, Zap, Shield, Swords, PlayCircle } from "lucide-react";
import QuickTour from "../components/QuickTour";

const FANDOM_OPTIONS = [
  "Bollywood", "Cricket", "KKR", "CSK", "MI", "RCB",
  "SRK", "Virat", "Dhoni", "BTS", "Bigg Boss", "Creators",
  "South Cinema", "F1", "Music", "Reality TV"
];

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const [bio, setBio] = useState(user?.bio || "");
  const [fandoms, setFandoms] = useState(user?.fandoms || []);
  const [editing, setEditing] = useState(false);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    getProfileStats().then(setStats).catch(() => {});
    getStatus().then(s => { setStatus(s); if (s.user) setUser(s.user); }).catch(() => {});
  }, []); // eslint-disable-line

  const toggleFandom = (f) => {
    setFandoms(fandoms.includes(f) ? fandoms.filter(x => x !== f) : [...fandoms, f]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const u = await updateProfile({ bio, fandoms });
      setUser(u);
      toast.success("Profile updated");
      setEditing(false);
    } catch {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  const tier = typeof status?.tier === "string" ? status.tier : null;
  const credits = user?.fan_credits ?? 0;
  const nextMs = status?.next_milestone;
  const progress = nextMs && nextMs.credits ? Math.min(1, credits / nextMs.credits) : 0;

  return (
    <div className="min-h-screen pb-safe" data-testid="profile-page">
      <Header title="Profile" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        <div className="glass-strong rounded-3xl p-5 relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-30" style={{ background: "radial-gradient(circle,#00F0FF,transparent 70%)" }} />
          <div className="flex items-center gap-4">
            <Avatar name={user?.name || "Fan"} category="default" size={80} rounded={18} />
            <div className="flex-1 min-w-0">
              <p className="font-display text-2xl font-bold truncate">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {tier && <TierBadge tier={tier} size="sm" />}
                <span className="chip" style={{ background: "#00FF6618", color: "#00FF66", border: "1px solid #00FF6655" }}>
                  <Zap className="w-3 h-3" /> {credits.toLocaleString("en-IN")} credits
                </span>
              </div>
            </div>
          </div>
          {nextMs && (
            <div className="mt-4">
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full" style={{ width: `${(progress * 100).toFixed(0)}%`, background: "linear-gradient(90deg,#00F0FF,#B026FF)" }} />
              </div>
              <p className="text-[11px] text-white/55 mt-1">
                {Math.max(0, nextMs.credits - credits).toLocaleString("en-IN")} credits to <b className="text-white">{nextMs.name}</b>
              </p>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest font-bold text-white/50">Bio</p>
            <button className="text-xs text-[#00F0FF] inline-flex items-center gap-1" onClick={() => setEditing(!editing)} data-testid="edit-profile-btn">
              {editing ? <><Check className="w-3 h-3" /> Done</> : <><Edit3 className="w-3 h-3" /> Edit</>}
            </button>
          </div>
          {editing ? (
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3}
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[#B026FF]/50"
              data-testid="bio-input" />
          ) : (
            <p className="text-sm text-white/75 mt-1">{user?.bio || "—"}</p>
          )}

          <p className="text-xs uppercase tracking-widest font-bold text-white/50 mt-4">Fandoms</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {(editing ? FANDOM_OPTIONS : fandoms).map((f) => {
              const active = fandoms.includes(f);
              return (
                <button key={f} data-testid={`fandom-${f}`}
                  onClick={() => editing && toggleFandom(f)}
                  className="chip"
                  style={{
                    background: active ? "#B026FF22" : "rgba(255,255,255,0.05)",
                    color: active ? "#B026FF" : "#fff",
                    border: active ? "1px solid #B026FF66" : "1px solid rgba(255,255,255,0.1)",
                    cursor: editing ? "pointer" : "default",
                  }}
                >{f}</button>
              );
            })}
            {!editing && fandoms.length === 0 && <span className="text-sm text-white/40">Add your fandoms.</span>}
          </div>

          {editing && (
            <button onClick={save} disabled={saving} data-testid="save-profile-btn"
              className="mt-4 w-full py-3 rounded-full font-bold text-black"
              style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)" }}>
              {saving ? "Saving…" : "Save Profile"}
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Stat label="Lineups" val={stats?.lineups ?? 0} />
          <Stat label="Trades" val={stats?.trades ?? 0} />
          <Stat label="Best pts" val={Math.round(stats?.best_lineup_points ?? 0)} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Link to="/my-circles" className="glass rounded-2xl p-4 card-hover" data-testid="go-portfolio">
            <p className="text-xs tracking-widest uppercase text-white/50 font-bold">My Circles</p>
            <p className="font-display font-semibold mt-1">Lineups, holdings & P&L</p>
          </Link>
          <Link to="/leaderboard" className="glass rounded-2xl p-4 card-hover" data-testid="go-leaderboard">
            <p className="text-xs tracking-widest uppercase text-white/50 font-bold">Leaderboard</p>
            <p className="font-display font-semibold mt-1">Ranks · XP · Contests</p>
          </Link>
          <Link to="/duels" className="glass rounded-2xl p-4 card-hover" data-testid="go-duels">
            <div className="flex items-center gap-2">
              <Swords className="w-4 h-4 text-[#FF0055]" />
              <p className="text-xs tracking-widest uppercase text-white/50 font-bold">My Duels</p>
            </div>
            <p className="font-display font-semibold mt-1">1v1 friend battles</p>
          </Link>
          <button
            onClick={() => setTourOpen(true)}
            data-testid="go-tutorial"
            className="glass rounded-2xl p-4 card-hover text-left"
          >
            <div className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-[#00F0FF]" />
              <p className="text-xs tracking-widest uppercase text-white/50 font-bold">Tutorial</p>
            </div>
            <p className="font-display font-semibold mt-1">Replay onboarding</p>
          </button>
        </div>

        {status?.admin && (
          <Link to="/admin" className="glass rounded-2xl p-4 flex items-center gap-3 card-hover" data-testid="go-admin">
            <Shield className="w-5 h-5 text-[#FF0055]" />
            <div>
              <p className="font-display font-semibold">Admin Dashboard</p>
              <p className="text-xs text-white/55">Economy & reward controls</p>
            </div>
          </Link>
        )}

        <button onClick={async () => { await logout(); window.location.href = "/"; }}
          data-testid="logout-btn"
          className="w-full py-3 rounded-full glass hover:bg-white/10 inline-flex items-center justify-center gap-2 text-white/80">
          <LogOut className="w-4 h-4" /> Log out
        </button>

        <p className="text-[10px] text-center text-white/30 mt-4 leading-relaxed">
          Fanpuff is a free-to-play entertainment fantasy and social experience. Fan Credits and Fanpuff XP have no cash value. No deposits, withdrawals, or real-money gaming are involved. Rewards are promotional, tier-based, and subject to eligibility and platform rules.
        </p>
        <div className="h-10" />
      </div>
      <BottomNav />
      <QuickTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
}

function Stat({ label, val }) {
  return (
    <div className="glass rounded-2xl p-3 text-center">
      <p className="text-[10px] tracking-widest uppercase text-white/50">{label}</p>
      <p className="font-display text-lg font-semibold mt-1">{typeof val === "number" ? val.toLocaleString("en-IN") : val}</p>
    </div>
  );
}
